import React, { useEffect, useState } from 'react';
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useSelector } from "react-redux";
import { getUserTypeName } from '../../../navigation/adminNavigation';
import { SegmentField, SelectField, TextField, Section } from '../../../../components/common/formkit/fields';
import { FormSync, FocusFirstError } from '../../../../components/common/formkit/formHelpers';

const Step1New = ({ form, setForm, nextStep, formId }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const authState = useSelector((state) => state.auth);
  const userType = getUserTypeName(authState);
  // const token = loggedInUser?.token;
  const token = loggedInUser?.refreshToken;

  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [propertyPurposes, setPropertyPurposes] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [propertySubtypes, setPropertySubtypes] = useState([]);
  const [propertySubSubTypes, setPropertySubSubTypes] = useState([]);
  const [landTypes, setLandTypes] = useState([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState(form.propertyType || "");
  const [selectedPropertySubtype, setSelectedPropertySubtype] = useState(form.propertySubtype || "");
  const [propertySubSubType, setPropertySubSubType] = useState();

  const postData = {
    page_number: 1,
    page_size: 20,
    order_by: "ASC",
  };

  // Fetch property purposes and types
  useEffect(() => {
    const fetchData = async () => {
      try {
        const authToken = token;
        const purposeResponse = await axios.post(
          `${baseUrl}/property/purposes`,
          postData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (purposeResponse.data.statusType === "SUCCESS") {
          // Same as mobile: "Promotion" is only offered to partners and super admins
          const purposes = purposeResponse.data.propertyPurposes || [];
          setPropertyPurposes(
            userType === "SUPER_ADMIN" || userType === "PARTNER"
              ? purposes
              : purposes.filter((p) => p.name !== "Promotion")
          );
        } else {
          setPropertyPurposes([]);
        }

        const typeResponse = await axios.post(
          `${baseUrl}/property/types`,
          postData,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (typeResponse.data.statusType === "SUCCESS") {
          setPropertyTypes(typeResponse.data.propertyTypes);
        } else {
          setPropertyTypes([]);
        }
      } catch (error) {
        setPropertyPurposes([]);
        setPropertyTypes([]);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token, userType]);

  // Fetch subtypes
  useEffect(() => {
    const fetchSubtypes = async (propertyTypeId) => {
      if (!propertyTypeId) {
        setPropertySubtypes([]);
        return;
      }
      try {
        const response = await axios.post(
          `${baseUrl}/property/sub/types/${propertyTypeId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setPropertySubtypes(response.data.propertySubTypes);
        } else {
          setPropertySubtypes([]);
        }
      } catch (error) {
        setPropertySubtypes([]);
      }
    };
    fetchSubtypes(selectedPropertyType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyType, baseUrl, token]);

  // Fetch subsubtypes
  useEffect(() => {
    const fetchPropertySubSubTypes = async () => {
      if (!selectedPropertySubtype) {
        setPropertySubSubTypes([]);
        return;
      }
      try {
        const response = await axios.get(
          `${baseUrl}/all/property/sub/sub/types/${selectedPropertySubtype}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setPropertySubSubTypes(response.data.propertySubSubTypes);
          setPropertySubSubType(response.data.propertySubSubTypes[0]?.property_sub_type_name);
        } else {
          setPropertySubSubTypes([]);
        }
      } catch (error) {
        setPropertySubSubTypes([]);
      }
    };
    fetchPropertySubSubTypes();
  }, [selectedPropertySubtype, baseUrl, token]);

  // Fetch land types
  useEffect(() => {
    const fetchLandTypes = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/all/land/sub/types`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.statusType === "SUCCESS") {
          setLandTypes(response.data.landTypes);
        } else {
          setLandTypes([]);
        }
      } catch (error) {
        setLandTypes([]);
      }
    };
    fetchLandTypes();
  }, [baseUrl, token]);

  const validationSchema = Yup.object({
    propertyType: Yup.string().required("Property type is required"),
    propertyPurpose: Yup.string().required("Property Purpose is required"),
    propertySubtype: Yup.string().required("Property Subtype is required"),
    propertySubSubType: propertySubSubTypes.length > 0
      ? Yup.string().required("Property Sub Subtype is required")
      : Yup.string().nullable(),
    are: Yup.string().required("Area is required"),
    landType: Yup.string().required("Land Type is required"),
    // Same as mobile: loan availability is not asked for Rent (purpose id 3)
    loanAvailability: Yup.mixed().when("propertyPurpose", {
      is: (purpose) => Number(purpose) === 3,
      then: (schema) => schema.nullable(),
      otherwise: (schema) =>
        schema.test("loan-required", "Loan Availability is required", (v) => typeof v === "boolean"),
    }),
  });

  const initialValues = {
    propertyPurpose: form?.propertyPurpose || "",
    propertyPurposeName: form?.propertyPurposeName || "",
    propertyType: form?.propertyType || "",
    propertyTypeName: form?.propertyTypeName || "",
    propertySubtype: form?.propertySubtype || "",
    propertySubtypeName: form?.propertySubtypeName || "",
    propertySubSubType: form?.propertySubSubType || "",
    propertySubSubTypeName: form?.propertySubSubTypeName || "",
    are: form?.are || "",
    landType: form?.landType || "",
    landTypeName: form?.landTypeName || "",
    loanAvailability: typeof form?.loanAvailability === 'boolean'
      ? form.loanAvailability
      : form?.loanAvailability === "Yes"
        ? true
        : form?.loanAvailability === "No"
          ? false
          : null,
  };

  const handleSubmit = (values, { setSubmitting }) => {
    setForm(prev => ({
      ...prev,
      ...values,
    }));
    setSubmitting(false);
    if (nextStep) nextStep();
  };

  const toOptions = (list) => (list || []).map((item) => ({ value: item.id, label: item.name }));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values }) => (
        <Form id={formId} noValidate>
          <FormSync onChange={(v) => setForm((prev) => ({ ...prev, ...v }))} />
          <FocusFirstError />

          <Section title="I wish to" hint="Choose what you want to do with this property.">
            <SegmentField
              name="propertyPurpose"
              label="Listing purpose"
              required
              options={toOptions(propertyPurposes)}
              onSelect={(value, option) => setFieldValue('propertyPurposeName', option.label)}
            />
          </Section>

          {values.propertyPurpose && (
            <Section title="Property type">
              <div className="fk-grid">
                <SelectField
                  name="propertyType"
                  label="Property type"
                  required
                  options={toOptions(propertyTypes)}
                  onSelect={(value, option) => {
                    setFieldValue('propertyTypeName', option.label);
                    setFieldValue('propertySubtype', '');
                    setFieldValue('propertySubtypeName', '');
                    setFieldValue('propertySubSubType', '');
                    setFieldValue('propertySubSubTypeName', '');
                    setSelectedPropertySubtype('');
                    setSelectedPropertyType(value);
                  }}
                />
                {values.propertyType && (
                  <SelectField
                    name="propertySubtype"
                    label="Property subtype"
                    required
                    options={toOptions(propertySubtypes)}
                    disabled={!selectedPropertyType}
                    onSelect={(value, option) => {
                      setFieldValue('propertySubtypeName', option.label);
                      setFieldValue('propertySubSubType', '');
                      setFieldValue('propertySubSubTypeName', '');
                      setSelectedPropertySubtype(value);
                    }}
                  />
                )}
              </div>
            </Section>
          )}

          {values.propertySubtype && (
            <Section title="Property details">
              <div className="fk-grid fk-grid--3">
                <SelectField
                  name="propertySubSubType"
                  label={propertySubSubType ? `${propertySubSubType} types` : "Property sub sub type"}
                  required={propertySubSubTypes.length > 0}
                  options={toOptions(propertySubSubTypes)}
                  onSelect={(value, option) => setFieldValue('propertySubSubTypeName', option.label)}
                />
                <TextField name="are" label="Area" required placeholder="Enter area" />
                <SelectField
                  name="landType"
                  label="Land type"
                  required
                  options={toOptions(landTypes)}
                  onSelect={(value, option) => setFieldValue('landTypeName', option.label)}
                />
                <SegmentField
                  name="loanAvailability"
                  label="Loan availability"
                  required={Number(values.propertyPurpose) !== 3}
                  className="fk-col-full"
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </div>
            </Section>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default Step1New;
